import * as React from 'react';

import type {
  AudienceDelta,
  CohortAudience,
  Learner,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import { useCohortMembershipCheck } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortMembershipCheck';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import { useServerLearnerPaging } from 'bundles/enterprise-admin-messaging-v2/hooks/useServerLearnerPaging';
import type { UserFilter } from 'bundles/enterprise-admin-user-filters/types';

type Params = {
  cohortAudience?: CohortAudience;
  /** Delta to seed the editor with when re-opening. */
  initialDelta?: AudienceDelta;
  /** Learner objects for `initialDelta.addedIds`, to seed the map on re-open. */
  initialAddedLearners?: Learner[];
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  filteredCount: number;
  loading: boolean;
  error: boolean;
  addLearner: (learner: Learner) => void;
  removeLearner: (id: string) => void;
  delta: AudienceDelta;
  /** Full Learner objects for learners in `delta.addedIds`. */
  addedLearners: Learner[];
};

/**
 * Builds an org-scoped MULTI_TYPE_FILTER that fetches both INVITED and JOINED
 * learners — the full pool from which the admin can add/remove for the cohort.
 */
const buildOrgLearnerFilter = (): UserFilter => ({
  primaryFilter: {
    type: 'MULTI_TYPE_FILTER',
    multiFilter: {
      membershipStates: ['INVITED', 'JOINED'],
    },
  },
});

/**
 * Editor hook for the AI cohort (dynamic cohort resolution) flow's "Add or
 * remove learners" screen. Instead of browsing the cohort itself, the admin
 * browses the full org pool (INVITED + JOINED via `adminuserfilter`) with
 * server-side pagination and search. For each page of results, parallel cohort
 * membership checks determine whether a learner is already in the AI-generated
 * cohort (show trash icon) or outside it (show + Add).
 *
 * Edits are tracked as a bidirectional delta (`addedIds` + `removedIds`).
 * The hook is skipped (no-op) when `cohortAudience` is falsy.
 */
export const useCohortLearnerEditor = ({ cohortAudience, initialDelta, initialAddedLearners }: Params): Result => {
  const [addedIds, setAddedIds] = React.useState<Set<string>>(() => new Set(initialDelta?.addedIds));
  const [removedIds, setRemovedIds] = React.useState<Set<string>>(() => new Set(initialDelta?.removedIds));
  const [addedLearnerMap, setAddedLearnerMap] = React.useState<Map<string, Learner>>(
    () => new Map(initialAddedLearners?.map((l) => [l.id, l]))
  );

  // Build the org-wide audience for adminuserfilter pagination.
  const orgAudience: LearnerListAudience | undefined = React.useMemo(() => {
    if (!cohortAudience) return undefined;
    return { orgId: cohortAudience.orgId, userFilter: buildOrgLearnerFilter() };
  }, [cohortAudience]);

  // Server-side paginated pool of all org learners (INVITED + JOINED).
  const {
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageCount,
    learners,
    total,
    loading: poolLoading,
    error: poolError,
  } = useServerLearnerPaging({ audience: orgAudience });

  // For the current page's learners, check cohort membership in parallel.
  const emailsForCheck = React.useMemo(() => learners.map((l) => l.email), [learners]);
  const {
    memberEmails,
    loading: membershipLoading,
    error: membershipError,
  } = useCohortMembershipCheck({
    cohortAudience,
    emails: emailsForCheck,
  });

  // Derive page rows: inList is true when the learner is effectively in the
  // cohort (either a natural member not removed, or explicitly added).
  const pageRows: LearnerRow[] = React.useMemo(() => {
    return learners.map((learner) => {
      const isNaturalMember = memberEmails.has(learner.email);
      let inList: boolean;
      if (addedIds.has(learner.id)) {
        inList = true;
      } else if (removedIds.has(learner.id)) {
        inList = false;
      } else {
        inList = isNaturalMember;
      }
      return { learner, inList };
    });
  }, [learners, memberEmails, addedIds, removedIds]);

  const addLearner = React.useCallback((learner: Learner) => {
    const id = learner.id;
    setRemovedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setAddedIds((prev) => new Set(prev).add(id));
    setAddedLearnerMap((prev) => new Map(prev).set(id, learner));
  }, []);

  const removeLearner = React.useCallback((id: string) => {
    setAddedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setRemovedIds((prev) => new Set(prev).add(id));
    setAddedLearnerMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const delta: AudienceDelta = React.useMemo(
    () => ({ addedIds: [...addedIds], removedIds: [...removedIds] }),
    [addedIds, removedIds]
  );

  const addedLearners: Learner[] = React.useMemo(() => [...addedLearnerMap.values()], [addedLearnerMap]);

  return {
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageCount,
    pageRows,
    filteredCount: total,
    loading: poolLoading || membershipLoading,
    error: poolError || membershipError,
    addLearner,
    removeLearner,
    delta,
    addedLearners,
  };
};

export default useCohortLearnerEditor;
